import React, { FC, useEffect, useMemo, useState } from 'react';
import { IConsent, USER_CONSENT_FIELDS } from '../types/consent';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { ErrorOverlayDataGrid } from './ErrorOverlayDataGrid';
import { GridRenderCellParams } from '@mui/x-data-grid/models/params/gridCellParams';
import { GridTooltip } from './General/GridTooltip';
import { IdentityHookResult } from '../hooks/useIdentity';
import { ErrorBoundary } from 'react-error-boundary';
import { getRowIndex, withRowIndex } from '../util/gridRowIndex';

interface UserConsentDataGridProps {
  identityHook: IdentityHookResult;
}

const UserConsentDataGrid: FC<UserConsentDataGridProps> = ({ identityHook }) => {
  const { identity } = identityHook;
  const [consents, setConsents] = useState<IConsent[]>([]);

  useEffect(() => {
    if (!identity) return;
    identity.consents && setConsents(identity.consents);
  }, []);

  const rows = useMemo(() => withRowIndex(consents), [consents]);

  const columns: GridColDef[] = [
    {
      field: USER_CONSENT_FIELDS.CONSENT_ID,
      headerName: 'Consent Id',
      flex: 1,
      renderCell: (params: GridRenderCellParams) => <GridTooltip params={params} />,
    },
    {
      field: USER_CONSENT_FIELDS.CONSENTED_ON,
      headerName: 'Consented On',
      flex: 1,
      valueGetter: (_value, row) => {
        const date = new Date(row.consentedOn);
        return `${row.consentedOn ? date.toLocaleString(undefined, { hour12: false }) : ''}`;
      },
      renderCell: (params: GridRenderCellParams) => <GridTooltip params={params} />,
    },
  ];

  return (
    <ErrorBoundary FallbackComponent={ErrorOverlayDataGrid}>
      {identity && (
        <DataGrid
          autoHeight
          columns={columns}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 5,
                page: 0,
              },
            },
          }}
          getRowId={getRowIndex}
          disableEval
          pageSizeOptions={[5, 10, 50]}
          rows={rows}
          sx={{
            backgroundColor: 'white',
          }}
        />
      )}
    </ErrorBoundary>
  );
};

export default UserConsentDataGrid;
